import { action, makeObservable, observable, toJS } from 'mobx';

import {
  MonetizationEventsEventType,
  type SportMonetizationCurrentEvent,
} from '../../../dto/monetization-events.types';
import { postRequest } from '../../../services/api';
import { getUserStats } from '../../../services/launchDarkly';
import type { ApiResponse } from '../../../services/types';
import { getErrorMessage } from '../../../utils/errors';
import { devError } from '../../../utils/log';
import type { FeatureManagerDialogType } from '../../feature-manager/types';
import type { LaunchGameData } from '../../game/types';
import { isEventLive, watchWarmFrame, WarmPrelaunch } from '../../metagames-warm/warmPrelaunch';
import type { RootStore } from '../../RootStore';
import { EnumOpenDialog } from '../../ui/types';

import { SPORT_BASE_PATH, SPORT_EVENTS_PATHS, SPORTS_DIALOG_TYPES } from './constants';

/**
 * Sports gets no server-minted access token: game-ui logs in with the launch token itself, and core keeps
 * that token for 5 minutes. Trust a prelaunch for well under that, so the login still has room after it.
 */
const SPORTS_PRELAUNCH_TTL_MS = 2 * 60 * 1000;

export class SportEventsStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    // Sports opens in the warm frame like the metagames, with no opt-in gate there.
    watchWarmFrame({
      rootStore,
      isEventLive: () => isEventLive(this.sportEvent),
      getPrelaunchTarget: () => (this.sportEvent ? { gameId: this.sportEvent.gameId, coin: 'ALL' } : null),
      prelaunch: this.prelaunch,
      preloadGame: 'sports',
    });
  }

  private readonly prelaunch = new WarmPrelaunch<'ALL'>(
    'SportEventsStore',
    (coin, gameId) => this.launchGame(coin, gameId),
    { ttlMs: SPORTS_PRELAUNCH_TTL_MS, isUsable: (launchGameData) => !!launchGameData.sportsEvents?.token },
  );

  private async launchGame(coin: 'ALL', gameId: number) {
    const response = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', { coin, gameId });

    return response.data?.data;
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public sportEvent: SportMonetizationCurrentEvent | undefined = undefined;

  @action
  public setSportEvent(event: SportMonetizationCurrentEvent | undefined) {
    this.sportEvent = event;
  }

  @observable
  public currentOpenSportEvent: SportMonetizationCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenSportEvent(event: SportMonetizationCurrentEvent | undefined) {
    this.currentOpenSportEvent = event;
    this.rootStore.monetizationEventsStore.setCurrentOpenSportEvent(event);
  }

  public iframeRef: HTMLIFrameElement | null = null;

  public setIframeRef = (iframe: HTMLIFrameElement | null) => {
    this.iframeRef = iframe;
  };

  public closeGame() {
    // Every exit must reach this: launch() refuses while 'live', so one missed exit makes every later
    // open — sports or metagame — fall back to a dialog for the rest of the session.
    if (this.rootStore.metagamesWarmStore.activeKey === 'sports') {
      this.rootStore.metagamesWarmStore.close();
    }

    this.setCurrentOpenSportEvent(undefined);
    this.rootStore.monetizationEventsStore.onMonetizationEventsDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();

    const dialogType = this.sportEvent ? SPORTS_DIALOG_TYPES[this.sportEvent.gameNameType] : undefined;

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  @action
  public reset() {
    this.sportEvent = undefined;
    this.currentOpenSportEvent = undefined;
    this.isLoadingGame = false;
    this.iframeRef = null;
    this.prelaunch.clear();
  }

  public async startSportEvent() {
    if (this.isLoadingGame) {
      return;
    }

    try {
      this.setIsLoadingGame(true);
      const sportEvent = this.sportEvent;
      if (!sportEvent) {
        devError('SportEventsStore: startSportEvent error: sport Event not found');
        return;
      }

      const launchGameData =
        (await this.prelaunch.take(sportEvent.gameId, 'ALL')) ?? (await this.launchGame('ALL', sportEvent.gameId));

      if (!launchGameData) {
        devError('SportEventsStore: startSportEvent error: launchGameResponse not found');
        return;
      }

      const { sportsEvents } = launchGameData;

      if (sportsEvents) {
        const { token } = sportsEvents;
        const { gameUiFlag } = this.rootStore.gameStore;

        let path = '';

        if (Object.values(SPORT_EVENTS_PATHS).includes(sportsEvents.path)) {
          path = `${SPORT_BASE_PATH}/${sportsEvents.path}`;
        } else {
          devError('SportEventsStore: startSportEvent error: invalid path');
          return;
        }

        const sportEventUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: 'ALL',
          eventType: MonetizationEventsEventType.SPORTS,
        });

        const sportEventToSet = {
          ...sportEvent,
          token,
          path,
          url: sportEventUrl,
          startDate: sportEvent.startDate,
          endDate: sportEvent.endDate,
        };

        this.setCurrentOpenSportEvent(sportEventToSet);

        // No flag of its own: metagames_warm_launch already decides whether a warm frame exists at all,
        // and without one launch() returns false. launch() also refuses while still warming or while
        // another game is live — the dialog below stays the fallback, as on every other warm game.
        const launchedWarm = this.rootStore.metagamesWarmStore.launch('sports', 'launchSportEvent', {
          token,
          path: sportsEvents.path,
        });

        if (!launchedWarm) {
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenMonetizationEventsDialog,
            options: { type: MonetizationEventsEventType.SPORTS },
          });
        }
      } else {
        devError('SportEventsStore: startSportEvent error: sport event not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('SportEventsStore: startSportEvent error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  sendSportEventGameUiMessage({ type, payload }: { type: string; payload: any }) {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      devError('SportEventsStore sendSportEventGameUiMessage: Iframe content window is not available');
      return;
    }

    this.iframeRef.contentWindow.postMessage(
      {
        type,
        payload,
      },
      import.meta.env.VITE_GAME_UI_URL,
    );
  }

  public handleSportEventIframeLoad(iframeRef: HTMLIFrameElement, gameType: string) {
    if (!iframeRef.contentWindow) {
      devError('SportEventsStore handleSportEventIframeLoad: Iframe content window is not available');
      return;
    }

    this.setIframeRef(iframeRef);

    if (this.currentOpenSportEvent) {
      this.sendSportEventGameUiMessage({
        type: 'newSportEvent',
        payload: {
          sportEvent: JSON.parse(JSON.stringify(toJS(this.currentOpenSportEvent))),
          gameType,
        },
      });

      if (this.rootStore.userStore.profile) {
        this.sendSportEventGameUiMessage({
          type: 'userReady',
          payload: {
            userProfile: getUserStats(this.rootStore.userStore.profile?.stats),
            language: this.rootStore.userStore.profile.language,
          },
        });
      }
    }
  }

  public startDialogs(type: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (!this.sportEvent || !this.sportEvent.displayPopup) return false;

    if (type === SPORTS_DIALOG_TYPES[this.sportEvent.gameNameType]) {
      void this.startSportEvent();
      return true;
    }

    return false;
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.monetizationEventsStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: this.currentOpenSportEvent?.gameNameType,
    });
  }
}
